import React, { useState } from 'react';
import { connect } from 'react-redux';
import { AgGridReact } from 'ag-grid-react';
import { Link } from 'react-router-dom';
import * as Bixby from '$/core';
import BixButton from '$/components/global/bixButton';
import { ReactComponent as DocumentIcon } from '$/assets/icons/DocumentWithTextSecondary.svg';
import { ReactComponent as EyeIcon } from '$/assets/icons/EyeIcon.svg';
import { ReactComponent as PortalLinkIcon } from '$/assets/icons/PortalLinkIcon.svg';
import HeadingBlock from '$/components/global/heading';
import Loading from '$/components/loading/Loading';
import NoDataFound from '$/components/global/noData';
import { toastError, toastSuccess } from '$/store/toast/actions';
import './index.scss';
import { WorkflowManagementTask } from '$/core/models';
import { MockData } from '$/pages/admin/workflow-management/mock';
import {
  WorkflowManagementCommonState,
  WorkflowManagementStatus,
  WorkflowManagementUploadToPortalState,
} from '$/core/enums';

interface IWorkflowManagementProps {
  assigneeList?: Bixby.Models.IIdentifiable[];
}

const WorkflowManagement: React.FC<IWorkflowManagementProps> = props => {
  const { assigneeList } = props;
  const [isLoading] = useState(false);
  const [isUpdating] = useState(false);
  const [errorMessage] = useState<string | undefined>();
  const [tasks] = useState<WorkflowManagementTask[]>(MockData);

  const statusCounts = {
    received: tasks.filter(t => t.status === WorkflowManagementStatus.Received).length,
    pendingReview: tasks.filter(t => t.status === WorkflowManagementStatus.PendingReview).length,
    inProgress: tasks.filter(t => t.status === WorkflowManagementStatus.InProgress).length,
    opsReview: tasks.filter(t => t.status === WorkflowManagementStatus.OpsReview).length,
    complete: tasks.filter(t => t.status === WorkflowManagementStatus.Complete).length,
  };

  const getContextMenuItems = (params: any) => {
    if (params.column.colId === 'name') {
      return [];
    }

    const result = ['copy', 'copyWithHeaders', 'separator', 'export'];
    return result;
  };

  const linkCellFormatter = (params: any) => {
    const url = `/admin/company/` + params.data['companyId'];
    return (
      <Link className="link_underline bix-link" to={url}>
        {' '}
        {` ${params.value} `}{' '}
      </Link>
    );
  };

  const OpenAdminControlsPopup = () => {
    // need to implement
  };

  if (isLoading) {
    return <Loading pageLevel />;
  } else {
    return (
      <div className="workflow_management_container">
        <div className="workflow_management_header">
          <HeadingBlock title={'Workflow Management Tool'} />
          <div className="workflow_management_admin_control">
            <BixButton isLoading={isUpdating} disabled={isUpdating} onClick={OpenAdminControlsPopup}>
              Admin Controls
            </BixButton>
          </div>
        </div>
        <div className="workflow_management_cards">
          <div className="workflow_management_card">
            <span>Received</span>
            <span className="workflow_management_secondary_color">{statusCounts.received}</span>
          </div>
          <div className="workflow_management_card">
            <span>Pending Review</span>
            <span className="workflow_management_secondary_color">{statusCounts.pendingReview}</span>
          </div>
          <div className="workflow_management_card">
            <span>In Progress</span>
            <span className="workflow_management_secondary_color">{statusCounts.inProgress}</span>
          </div>
          <div className="workflow_management_card">
            <span>Ops Review</span>
            <span className="workflow_management_secondary_color">{statusCounts.opsReview}</span>
          </div>
          <div className="workflow_management_card">
            <span>Complete</span>
            <span className="workflow_management_secondary_color">{statusCounts.complete}</span>
          </div>
        </div>
        <div className="workflow_management_tasks_container">
          <div className="workflow_management_tasks_summary">
            <h3>Doc Upload Tasks</h3>
            <span>
              {tasks.length} {tasks.length === 1 ? 'task' : 'tasks'}
            </span>
          </div>
          {tasks && !Bixby.Utils.isNullOrEmpty(tasks) ? (
            <section className="workflow_management_tasks_list">
              <div className="workflow_management_data_grid">
                <div className="grid_container">
                  <div className="grid ag-theme-alpine">
                    <AgGridReact
                      enableRangeSelection={true}
                      getContextMenuItems={getContextMenuItems}
                      defaultColDef={{
                        flex: 1,
                        minWidth: 200,
                        sortable: true,
                        filter: 'agTextColumnFilter',
                        cellClass: 'align-left',
                      }}
                      rowData={tasks}
                      suppressDragLeaveHidesColumns={true}
                      suppressMovableColumns={true}
                      animateRows={true}
                      rowHeight={32}
                      stopEditingWhenCellsLoseFocus={true}
                      domLayout="autoHeight"
                      headerHeight={32}
                      columnDefs={[
                        {
                          field: 'clientName',
                          headerName: 'Client Name',

                          cellRenderer: linkCellFormatter,
                        },
                        {
                          field: 'datasiteName',
                          headerName: 'Data Site Name',
                          cellClass: 'align-left',
                        },
                        { field: 'bixbyId', headerName: 'Bixby Id', sort: 'asc' },
                        {
                          field: 'provider',
                          headerName: 'Provider',
                          minWidth: 150,
                        },
                        {
                          field: 'docCount',
                          headerName: 'Doc Count',
                          minWidth: 150,
                          cellDataType: 'number',
                          cellClass: 'align-center',
                        },
                        {
                          field: 'docs',
                          headerName: 'Documents',
                          autoHeight: true,
                          cellRenderer: DocsCellRenderer,
                        },
                        {
                          field: 'newsType',
                          headerName: 'News Type',
                          width: 100,
                        },
                        {
                          field: 'recievedClients',
                          headerName: 'Recieved Clients',
                          width: 100,
                          autoHeight: true,
                          cellRenderer: (params: any) => {
                            return (
                              <div className="workflow-management-clients">
                                {params.value.map(client => {
                                  return (
                                    <span
                                      key={`recieved-client-${client}`}
                                      className="workflow-management-recieved-client">
                                      {client}
                                    </span>
                                  );
                                })}
                              </div>
                            );
                          },
                        },
                        {
                          field: 'missingClients',
                          headerName: 'Missing Clients',
                          width: 100,
                          autoHeight: true,
                          cellRenderer: (params: any) => {
                            return (
                              <div className="workflow-management-clients">
                                {params.value.map(client => {
                                  return (
                                    <span
                                      key={`missing-client-${client}`}
                                      className="workflow-management-missing-client">
                                      {client}
                                    </span>
                                  );
                                })}
                              </div>
                            );
                          },
                        },
                        {
                          field: 'taskCreated',
                          headerName: 'Task Created',
                          width: 100,
                          cellDataType: 'dateTime',
                          valueFormatter: params => {
                            return new Date(params.value).toLocaleString('en-US', {
                              month: 'numeric',
                              day: 'numeric',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                            });
                          },
                        },
                        {
                          field: 'assignee',
                          headerName: 'Assignee',
                          width: 100,
                          editable: true,
                          floatingFilter: true,
                          cellEditor: 'agSelectCellEditor',
                          cellEditorParams: {
                            values: assigneeList?.map(_ => _.id) || [],
                          },
                        },
                        {
                          field: 'assignTask',
                          headerName: 'Assign Task',
                          width: 100,
                        },
                        {
                          field: 'uploadToPortal',
                          headerName: 'Upload To Portal',
                          width: 100,
                          editable: true,
                          cellEditor: 'agSelectCellEditor',
                          cellEditorParams: {
                            values: Object.values(WorkflowManagementUploadToPortalState),
                          },
                        },
                        {
                          field: 'transcription',
                          headerName: 'Transcription',
                          width: 100,
                          editable: true,
                          cellEditor: 'agSelectCellEditor',
                          cellEditorParams: {
                            values: Object.values(WorkflowManagementCommonState),
                          },
                        },
                        {
                          field: 'modelUpdate',
                          headerName: 'Model Update',
                          width: 100,
                          editable: true,
                          cellEditor: 'agSelectCellEditor',
                          cellEditorParams: {
                            values: Object.values(WorkflowManagementCommonState),
                          },
                        },
                        {
                          field: 'portalLink',
                          headerName: 'Portal Link',
                          width: 100,
                          cellRenderer: (params: any) => {
                            return (
                              <div className="workflow-management-view-portal">
                                <PortalLinkIcon />
                                <a href={params.value}>View Portal</a>
                              </div>
                            );
                          },
                        },
                        {
                          field: 'status',
                          headerName: 'Status',
                          width: 100,
                          editable: true,
                          cellEditor: 'agSelectCellEditor',
                          floatingFilter: true,
                          cellEditorParams: {
                            values: Object.values(WorkflowManagementStatus),
                          },
                        },
                        {
                          field: 'completedAt',
                          headerName: 'Completed At',
                          width: 100,
                        },
                      ]}></AgGridReact>
                  </div>
                </div>
              </div>
            </section>
          ) : (
            <>
              {errorMessage && <div>{errorMessage}</div>}
              <div className="workflow_management_no_data">
                <NoDataFound dataType="tasks" />
              </div>
            </>
          )}
        </div>
      </div>
    );
  }
};

function DocsCellRenderer({ value }) {
  if (!value?.length) return null;

  return (
    <div className="workflow-management-docs-cell">
      <div className="workflow-management-docs-list">
        {value.map(doc => {
          return (
            <div key={`doc-${doc}`} className="workflow-management-doc-item">
              <span className="workflow-management-doc-icon">
                <DocumentIcon />
              </span>
              <div>{doc}</div>
            </div>
          );
        })}
      </div>
      <button>
        <EyeIcon />
      </button>
    </div>
  );
}

const mapStateToProps = () => {
  return {};
};
const mapDispatchToProps = (dispatch: any) => ({
  dispatchToastError: (message: string) => dispatch(toastError(message)),
  dispatchToastSuccess: (message: string) => dispatch(toastSuccess(message)),
});

export default connect(mapStateToProps, mapDispatchToProps)(WorkflowManagement);
